import React from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { ActionRow } from './lib/selectors';
import { NoReviewChip, PriorityChip, RecommendedLabel, VerdictChip } from './chips';
import { ImpactTiles } from './ImpactTiles';
import { FOCUS_RING, FONT, RADIUS } from './lib/brand';

const clip = (s: string, n: number) => (s.length > n ? `${s.slice(0, n)}…` : s);

/**
 * One item, decidable at a glance: what it is, what would change, impact / effort /
 * risk / confidence, who proposed it and what the exec thinks, then the decision.
 * It carries `data-action-row`, so j / k / y / n reach these cards first.
 */
export function DecisionCard({
  row, rank, accent, renderDecision, onOpenAction, onOpenBot, onWhy, onTouch,
}: {
  row: ActionRow; rank: number; accent: string;
  renderDecision: (row: ActionRow) => React.ReactNode;
  onOpenAction: (id: string) => void; onOpenBot: (slug: string) => void; onWhy?: (row: ActionRow) => void;
  /** Called on any click inside the card, so a card Grant answers stays in the queue. */
  onTouch: (id: string) => void;
}) {
  const { action: a, bot } = row;
  const title = a.for || a.text || 'Untitled item';
  return (
    <Box
      role="group"
      tabIndex={0}
      aria-label={`${rank}. ${title}`}
      data-action-row={a.id}
      data-testid={`queue-${a.id}`}
      onClickCapture={() => onTouch(a.id)}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter') { e.preventDefault(); onOpenAction(a.id); }
      }}
      sx={{
        display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0,1fr) 230px' }, gap: { xs: 1.2, md: 2 },
        px: 2, py: 1.6, borderTop: '1px solid', borderColor: 'divider',
        '&:focus-visible': { outline: FOCUS_RING, outlineOffset: -3 },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap', mb: 0.6 }}>
          <Box component="span" aria-hidden sx={{ fontFamily: FONT.display, fontWeight: 700, fontSize: 18, color: 'primary.main', minWidth: 22 }}>{rank}</Box>
          <PriorityChip priority={a.priority} />
          {a.priority_code && <Box component="span" sx={{ fontFamily: FONT.mono, fontSize: 10.5, fontWeight: 700, px: 0.8, borderRadius: RADIUS.sm, bgcolor: 'action.selected' }}>{a.priority_code}</Box>}
          {a.review ? <VerdictChip exec={a.review.exec} verdict={a.review.verdict} /> : <NoReviewChip level="item" />}
        </Box>
        <Typography component="h3" sx={{ fontWeight: 700, fontSize: 14.5, lineHeight: 1.35 }}>{title}</Typography>
        <Box sx={{ mt: 0.8 }}>
          <RecommendedLabel action={a} accent={accent} />
          <Typography sx={{ fontSize: 13, mt: 0.5, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            <Box component="span" sx={{ fontWeight: 700 }}>What changes: </Box>{a.recommended_action ? clip(a.recommended_action, 420) : 'Not stated in source'}
          </Typography>
        </Box>
        <Box sx={{ mt: 1 }}><ImpactTiles action={a} /></Box>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mt: 1 }}>
          <ButtonBase onClick={() => onOpenBot(bot.slug)} sx={{ fontSize: 12.5, fontWeight: 700, color: 'primary.main' }}>{bot.name}</ButtonBase>
          {onWhy && <ButtonBase onClick={() => onWhy(row)} aria-label={`Why is "${clip(title, 60)}" here?`} sx={{ fontSize: 12, color: 'primary.main', textDecoration: 'underline' }}>Why?</ButtonBase>}
          <ButtonBase onClick={() => onOpenAction(a.id)} aria-label={`Open "${clip(title, 60)}"`} sx={{ fontSize: 12, color: 'primary.main', textDecoration: 'underline' }}>Open</ButtonBase>
        </Box>
      </Box>
      <Box sx={{ minWidth: 0 }}>{renderDecision(row)}</Box>
    </Box>
  );
}
